import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import {
  TableCellsIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CloudArrowUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { hmdaApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';

export default function HmdaValidationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { teamId } = useTeamId();
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading } = useQuery({
    // teamId is part of the key so each tenant gets its own cache entry — a
    // team switch can never show another tenant's validation list from cache.
    queryKey: ['hmda-validations', teamId, page],
    queryFn: async () => {
      const response = await hmdaApi.getValidations({ page, limit });
      return response.data;
    },
    enabled: !!teamId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => hmdaApi.deleteValidation(id),
    onSuccess: () => {
      toast.success('Validation deleted');
      queryClient.invalidateQueries({ queryKey: ['hmda-validations'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete');
    },
  });

  const handleDelete = (e: React.MouseEvent, id: string, fileName: string) => {
    e.stopPropagation(); // Prevent row click navigation
    if (window.confirm(`Delete validation for "${fileName}"? This cannot be undone.`)) {
      deleteMutation.mutate(id);
    }
  };

  const validations = data?.validations || [];
  const pagination = data?.pagination;
  const totalPages = pagination?.totalPages || 1;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">HMDA Validations</h1>
          <p className="text-sm text-gray-600 mt-1">
            History of all LAR file validation runs
          </p>
        </div>
        <Link
          to="/hmda/lar-validation"
          className="inline-flex items-center gap-2 rounded-lg bg-ocean-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-ocean-600 transition-colors"
        >
          <CloudArrowUpIcon className="h-4 w-4" />
          New Validation
        </Link>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading validations...</div>
      ) : validations.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <TableCellsIcon className="mx-auto h-12 w-12 text-gray-300" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No validations yet</h3>
          <p className="mt-2 text-sm text-gray-500">
            Upload a HMDA LAR file to run your first validation.
          </p>
          <Link
            to="/hmda/lar-validation"
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-ocean-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-ocean-600 transition-colors"
          >
            <CloudArrowUpIcon className="h-4 w-4" />
            Upload LAR File
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Institution
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Loans
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Issues
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {validations.map((v: any) => {
                const hasErrors = v.syntacticalErrors > 0 || v.validityErrors > 0;
                const isClean = v.totalIssues === 0;
                return (
                  <tr
                    key={v.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/hmda/validations/${v.id}`)}
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                        {v.fileName}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 truncate max-w-[180px]">
                        {v.institutionName || '--'}
                      </div>
                      <div className="text-xs text-gray-400">{v.lei || ''}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {v.totalLoans?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      {isClean ? (
                        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircleIcon className="h-3.5 w-3.5" />
                          Ready
                        </span>
                      ) : hasErrors ? (
                        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium bg-red-100 text-red-700">
                          <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                          Errors
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700">
                          <MagnifyingGlassIcon className="h-3.5 w-3.5" />
                          Warnings
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        {v.syntacticalErrors + v.validityErrors > 0 && (
                          <span className="text-red-600 font-medium">
                            {v.syntacticalErrors + v.validityErrors} error{v.syntacticalErrors + v.validityErrors !== 1 ? 's' : ''}
                          </span>
                        )}
                        {v.syntacticalErrors + v.validityErrors > 0 && v.qualityWarnings + v.macroWarnings > 0 && (
                          <span className="text-gray-400"> / </span>
                        )}
                        {v.qualityWarnings + v.macroWarnings > 0 && (
                          <span className="text-amber-600">
                            {v.qualityWarnings + v.macroWarnings} warning{v.qualityWarnings + v.macroWarnings !== 1 ? 's' : ''}
                          </span>
                        )}
                        {v.totalIssues === 0 && (
                          <span className="text-green-600 font-medium">Clean</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDistanceToNow(new Date(v.createdAt), { addSuffix: true })}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={(e) => handleDelete(e, v.id, v.fileName)}
                        disabled={deleteMutation.isPending}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                        title="Delete validation"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-700">
                Page {page} of {totalPages}
                {pagination?.total && ` (${pagination.total} total)`}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="p-2 rounded-md border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  className="p-2 rounded-md border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
